/**
 * PDF Export Utility (UX-005)
 *
 * Provides client-side PDF export using the browser's print functionality.
 * For production, consider integrating with a backend service using Playwright.
 */

export interface ExportOptions {
  title?: string;
  filename?: string;
  orientation?: 'portrait' | 'landscape';
}

/**
 * Export a specific DOM element to PDF using browser print dialog
 */
export function exportToPDF(elementId: string, options: ExportOptions = {}) {
  const element = document.getElementById(elementId);
  if (!element) {
    console.error(`Element with id "${elementId}" not found`);
    return;
  }

  const {
    title = 'Executive Summary',
    filename = 'executive-summary',
  } = options;

  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Please allow pop-ups to export PDF');
    return;
  }

  // Get the HTML content
  const content = element.innerHTML;

  // Build the print document with styling
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          padding: 40px;
          color: #1a1a1a;
          line-height: 1.6;
        }
        .card {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 24px;
          margin-bottom: 16px;
        }
        h1, h2, h3 {
          color: #1a1a1a;
          margin-bottom: 12px;
        }
        .text-white { color: #1a1a1a; }
        .text-text-secondary, .text-text-secondary, .text-text-tertiary { color: #666; }
        .text-primary-400, .text-primary-300 { color: #3b82f6; }
        .text-success-400 { color: #22c55e; }
        .text-warning-400 { color: #f59e0b; }
        .text-danger-400 { color: #ef4444; }
        .font-bold { font-weight: 700; }
        .font-semibold { font-weight: 600; }
        .font-medium { font-weight: 500; }
        .text-xs { font-size: 0.75rem; }
        .text-sm { font-size: 0.875rem; }
        .text-lg { font-size: 1.125rem; }
        .text-xl { font-size: 1.25rem; }
        .text-2xl { font-size: 1.5rem; }
        .mb-2 { margin-bottom: 0.5rem; }
        .mb-4 { margin-bottom: 1rem; }
        .gap-2 { gap: 0.5rem; }
        .gap-3 { gap: 0.75rem; }
        .gap-4 { gap: 1rem; }
        .flex { display: flex; }
        .flex-wrap { flex-wrap: wrap; }
        .items-center { align-items: center; }
        .justify-between { justify-content: space-between; }
        .grid { display: grid; }
        .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
        .grid-cols-4 { grid-template-columns: repeat(4, 1fr); }
        .rounded-lg { border-radius: 0.5rem; }
        .rounded-full { border-radius: 9999px; }
        .p-3 { padding: 0.75rem; }
        .p-4 { padding: 1rem; }
        .px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
        .py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
        .bg-surface-600, .bg-surface-700 { background: #f0f0f0; }
        .bg-success-500\\/10, .bg-success-500\\/20 { background: #dcfce7; }
        .bg-warning-500\\/10, .bg-warning-500\\/20 { background: #fef3c7; }
        .bg-primary-500\\/10, .bg-primary-500\\/20 { background: #dbeafe; }
        .border-l-2 { border-left-width: 2px; border-left-style: solid; }
        .border-l-success-500 { border-left-color: #22c55e; }
        .border-l-warning-500 { border-left-color: #f59e0b; }
        .border-l-gray-500 { border-left-color: #6b7280; }
        .uppercase { text-transform: uppercase; }
        .tracking-wide { letter-spacing: 0.025em; }
        .leading-relaxed { line-height: 1.625; }
        /* Hide interactive elements */
        button, .sr-only, [role="button"] { display: none !important; }
        svg { display: none; }
        /* Print-specific */
        @media print {
          body { padding: 20px; }
          .card { break-inside: avoid; }
        }
        /* Header */
        .print-header {
          text-align: center;
          margin-bottom: 32px;
          padding-bottom: 16px;
          border-bottom: 2px solid #e5e7eb;
        }
        .print-header h1 {
          font-size: 1.5rem;
          color: #1a1a1a;
        }
        .print-header p {
          color: #666;
          font-size: 0.875rem;
        }
        /* Footer */
        .print-footer {
          margin-top: 32px;
          padding-top: 16px;
          border-top: 1px solid #e5e7eb;
          text-align: center;
          font-size: 0.75rem;
          color: #9ca3af;
        }
      </style>
    </head>
    <body>
      <div class="print-header">
        <h1>${title}</h1>
        <p>Generated on ${new Date().toLocaleDateString()}</p>
      </div>
      ${content}
      <div class="print-footer">
        <p>CPG Simulator - Confidential</p>
      </div>
    </body>
    </html>
  `);

  printWindow.document.close();

  // Wait for content to load, then trigger print
  setTimeout(() => {
    printWindow.print();
    // Close after a delay to allow print dialog
    setTimeout(() => {
      printWindow.close();
    }, 1000);
  }, 250);
}

/**
 * Copy summary text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
}

/**
 * Generate plain text summary from data
 */
export function generateTextSummary(data: {
  headline: string;
  pillars: Array<{ label: string; insight: string }>;
  metrics?: Array<{ label: string; value: string }>;
}): string {
  let text = `EXECUTIVE SUMMARY\n`;
  text += `${'='.repeat(50)}\n\n`;
  text += `${data.headline}\n\n`;

  if (data.metrics && data.metrics.length > 0) {
    text += `KEY METRICS\n`;
    text += `${'-'.repeat(30)}\n`;
    data.metrics.forEach(m => {
      text += `• ${m.label}: ${m.value}\n`;
    });
    text += `\n`;
  }

  text += `5C ANALYSIS\n`;
  text += `${'-'.repeat(30)}\n`;
  data.pillars.forEach(p => {
    text += `\n[${p.label}]\n${p.insight}\n`;
  });

  text += `\n${'='.repeat(50)}\n`;
  text += `Generated: ${new Date().toLocaleString()}\n`;

  return text;
}
